import { useState, useMemo } from 'react'
import { CheckInRewardType } from '@/schema'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
	DialogDescription,
} from '@/common/ui/dialog'
import { Button } from '@/common/ui/button'
import { Input } from '@/common/ui/input'
import { Label } from '@/common/ui/label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { Badge } from '@/common/ui/badge'
import {
	computeCheckInDays,
	isGeneratorConfigValid,
	type GeneratorConfig,
	type GeneratorMode,
	type RewardItemConfig,
} from '../../utils/generateCheckInDays'
import { DAILY_CHECK_IN_REWARD_TYPE_LABELS } from '../../constants'
import type { DailyCheckInFormValues } from '../../components/FormSchema'
import {
	MAX_DAILY_CHECK_IN_DAYS,
	MIN_DAILY_CHECK_IN_DAYS,
} from '../../components/FormSchema'
import { cn } from '@/lib/utils'

const REWARD_TYPE_COLORS: Record<CheckInRewardType, string> = {
	DIAMOND: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
	CREDIT: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
	WHEEL:
		'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
	FREE_SPIN:
		'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
}

const DEFAULT_REWARD_ITEM: RewardItemConfig = {
	rewardType: CheckInRewardType.DIAMOND,
	amount: 10,
	start: 5,
	step: 5,
	max: 50,
}

interface Props {
	open: boolean
	onOpenChange: (open: boolean) => void
	onApply: (settings: DailyCheckInFormValues['checkInSetting']) => void
}

export function GenerateTemplateModal({ open, onOpenChange, onApply }: Props) {
	const [days, setDays] = useState(25)
	const [mode, setMode] = useState<GeneratorMode>('FLAT')
	const [rewardItemCount, setRewardItemCount] = useState(1)
	const [rewardItems, setRewardItems] = useState<RewardItemConfig[]>([
		{ ...DEFAULT_REWARD_ITEM },
		{ ...DEFAULT_REWARD_ITEM, rewardType: CheckInRewardType.CREDIT },
		{
			...DEFAULT_REWARD_ITEM,
			rewardType: CheckInRewardType.WHEEL,
			amount: 1,
			start: 1,
			step: 0,
			max: 1,
		},
		{
			...DEFAULT_REWARD_ITEM,
			rewardType: CheckInRewardType.FREE_SPIN,
			amount: 1,
			start: 1,
			step: 0,
			max: 1,
		},
	])

	const activeRewardItems = rewardItems.slice(0, rewardItemCount)

	const config: GeneratorConfig = { days, mode, rewardItems: activeRewardItems }
	const isValid = isGeneratorConfigValid(config)

	const preview = useMemo(() => {
		if (!isValid) return []
		return computeCheckInDays(config)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [days, mode, rewardItemCount, rewardItems, isValid])

	const hasFreeSpinRewardItem = activeRewardItems.some(
		(item) => item.rewardType === CheckInRewardType.FREE_SPIN,
	)

	function updateRewardItem(index: number, patch: Partial<RewardItemConfig>) {
		setRewardItems((prev) => {
			const next = [...prev]
			next[index] = { ...next[index], ...patch }
			return next
		})
	}

	function handleApply() {
		onApply(preview)
		onOpenChange(false)
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="flex max-h-[90vh] max-w-3xl flex-col gap-0 p-0">
				<DialogHeader className="border-b px-6 py-4">
					<DialogTitle>สร้างจากเทมเพลต</DialogTitle>
					<DialogDescription>
						กำหนดรูปแบบรางวัล แล้วระบบจะสร้างการตั้งค่าเช็คอินให้อัตโนมัติ
					</DialogDescription>
				</DialogHeader>

				<div className="flex min-h-0 flex-1 flex-col gap-0 overflow-hidden lg:flex-row">
					{/* Left: Config panel */}
					<div className="flex flex-col gap-5 overflow-y-auto border-b p-6 lg:w-80 lg:shrink-0 lg:border-b-0 lg:border-r">
						{/* Days */}
						<div className="space-y-1.5">
							<Label>จำนวนวัน</Label>
							<Select
								value={String(days)}
								onValueChange={(v) => setDays(Number(v))}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{Array.from(
										{
											length:
												MAX_DAILY_CHECK_IN_DAYS - MIN_DAILY_CHECK_IN_DAYS + 1,
										},
										(_, i) => i + MIN_DAILY_CHECK_IN_DAYS,
									).map((d) => (
										<SelectItem key={d} value={String(d)}>
											{d} วัน
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						{/* Behavior */}
						<div className="space-y-1.5">
							<Label>รูปแบบรางวัล</Label>
							<div className="grid grid-cols-2 gap-2">
								{(['FLAT', 'INCREMENT'] as GeneratorMode[]).map((m) => (
									<button
										key={m}
										type="button"
										onClick={() => setMode(m)}
										className={cn(
											'rounded-md border px-3 py-2 text-sm font-medium transition-colors',
											mode === m
												? 'border-primary bg-primary text-primary-foreground'
												: 'border-input bg-background hover:bg-accent',
										)}
									>
										{m === 'FLAT' ? 'คงที่' : 'เพิ่มขึ้น'}
									</button>
								))}
							</div>
							<p className="text-xs text-muted-foreground">
								{mode === 'FLAT'
									? 'รางวัลเท่ากันทุกวันที่รางวัลนั้นปรากฏ'
									: 'รางวัลเพิ่มขึ้นทุกครั้งที่รางวัลนั้นปรากฏ จนถึงค่าสูงสุด'}
							</p>
						</div>

						{/* Slot count */}
						<div className="space-y-1.5">
							<Label>จำนวนประเภทรางวัล</Label>
							<div className="grid grid-cols-4 gap-2">
								{[1, 2, 3, 4].map((n) => (
									<button
										key={n}
										type="button"
										onClick={() => setRewardItemCount(n)}
										className={cn(
											'rounded-md border px-3 py-2 text-sm font-medium transition-colors',
											rewardItemCount === n
												? 'border-primary bg-primary text-primary-foreground'
												: 'border-input bg-background hover:bg-accent',
										)}
									>
										{n}
									</button>
								))}
							</div>
							{rewardItemCount > 1 && (
								<p className="text-xs text-muted-foreground">
									รางวัลจะสลับกันตามลำดับ (วนซ้ำ)
								</p>
							)}
						</div>

						{/* Reward item configs */}
						<div className="space-y-4">
							{activeRewardItems.map((rewardItem, i) => (
								<div key={i} className="space-y-3 rounded-lg border p-3">
									<p className="text-sm font-medium">
										{rewardItemCount === 1 ? 'รางวัล' : `รางวัลที่ ${i + 1}`}
									</p>

									<div className="space-y-1.5">
										<Label className="text-xs">ประเภทรางวัล</Label>
										<Select
											value={rewardItem.rewardType}
											onValueChange={(v) =>
												updateRewardItem(i, {
													rewardType: v as CheckInRewardType,
												})
											}
										>
											<SelectTrigger className="h-8 text-xs">
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{Object.values(CheckInRewardType).map((type) => (
													<SelectItem key={type} value={type}>
														{DAILY_CHECK_IN_REWARD_TYPE_LABELS[type]}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>

									{mode === 'FLAT' ? (
										<div className="space-y-1.5">
											<Label className="text-xs">จำนวน</Label>
											<Input
												type="number"
												min={0}
												className="h-8 text-xs"
												value={rewardItem.amount ?? ''}
												onChange={(e) =>
													updateRewardItem(i, {
														amount: e.target.value
															? Number(e.target.value)
															: undefined,
													})
												}
											/>
										</div>
									) : (
										<div className="grid grid-cols-3 gap-2">
											<div className="space-y-1.5">
												<Label className="text-xs">เริ่มต้น</Label>
												<Input
													type="number"
													min={0}
													className="h-8 text-xs"
													value={rewardItem.start ?? ''}
													onChange={(e) =>
														updateRewardItem(i, {
															start: e.target.value
																? Number(e.target.value)
																: undefined,
														})
													}
												/>
											</div>
											<div className="space-y-1.5">
												<Label className="text-xs">เพิ่มครั้งละ</Label>
												<Input
													type="number"
													min={0}
													className="h-8 text-xs"
													value={rewardItem.step ?? ''}
													onChange={(e) =>
														updateRewardItem(i, {
															step: e.target.value
																? Number(e.target.value)
																: undefined,
														})
													}
												/>
											</div>
											<div className="space-y-1.5">
												<Label className="text-xs">สูงสุด</Label>
												<Input
													type="number"
													min={0}
													className="h-8 text-xs"
													value={rewardItem.max ?? ''}
													onChange={(e) =>
														updateRewardItem(i, {
															max: e.target.value
																? Number(e.target.value)
																: undefined,
														})
													}
												/>
											</div>
										</div>
									)}

									{/* Eligibility requirements */}
									<div className="space-y-2 border-t pt-2">
										<p className="text-xs text-muted-foreground">
											เงื่อนไข (ไม่บังคับ)
										</p>
										<div className="grid grid-cols-2 gap-2">
											<div className="space-y-1.5">
												<Label className="text-xs">ยอดเทิร์นขั้นต่ำ</Label>
												<Input
													type="number"
													min={0}
													placeholder="0"
													className="h-8 text-xs"
													value={rewardItem.minimumTurnoverAmount ?? ''}
													onChange={(e) =>
														updateRewardItem(i, {
															minimumTurnoverAmount: e.target.value
																? Number(e.target.value)
																: undefined,
														})
													}
												/>
											</div>
											<div className="space-y-1.5">
												<Label className="text-xs">ยอดฝากขั้นต่ำ</Label>
												<Input
													type="number"
													min={0}
													placeholder="0"
													className="h-8 text-xs"
													value={rewardItem.minimumDepositAmount ?? ''}
													onChange={(e) =>
														updateRewardItem(i, {
															minimumDepositAmount: e.target.value
																? Number(e.target.value)
																: undefined,
														})
													}
												/>
											</div>
										</div>
									</div>
								</div>
							))}
						</div>

						{hasFreeSpinRewardItem && (
							<div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
								วันที่เป็น FREE_SPIN จะต้องตั้งค่า Preset, เกม
								และจำนวนเดิมพันเพิ่มเติมหลังจากสร้าง
							</div>
						)}
					</div>

					{/* Right: Preview */}
					<div className="flex flex-1 flex-col overflow-hidden">
						<div className="border-b px-4 py-3">
							<p className="text-sm font-medium">
								ตัวอย่าง{' '}
								<span className="text-muted-foreground">({days} วัน)</span>
							</p>
						</div>
						<div className="overflow-y-auto p-4">
							{isValid && preview.length > 0 ? (
								<div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
									{preview.map((item) => (
										<div
											key={item.day}
											className="flex flex-col items-center gap-1 rounded-lg border p-2 text-center"
										>
											<span className="text-xs text-muted-foreground">
												วัน {item.day}
											</span>
											<Badge
												variant="outline"
												className={cn(
													'px-1.5 py-0.5 text-xs font-medium',
													REWARD_TYPE_COLORS[
														item.rewardType as CheckInRewardType
													],
												)}
											>
												{
													DAILY_CHECK_IN_REWARD_TYPE_LABELS[
														item.rewardType as CheckInRewardType
													]
												}
											</Badge>
											<span className="text-sm font-semibold">
												{item.amount}
											</span>
											{item.minimumTurnoverAmount ||
											item.minimumDepositAmount ? (
												<div className="mt-0.5 space-y-0.5 text-center">
													{!!item.minimumTurnoverAmount && (
														<p className="text-[10px] leading-tight text-muted-foreground">
															เทิร์น {item.minimumTurnoverAmount}
														</p>
													)}
													{!!item.minimumDepositAmount && (
														<p className="text-[10px] leading-tight text-muted-foreground">
															ฝาก {item.minimumDepositAmount}
														</p>
													)}
												</div>
											) : null}
										</div>
									))}
								</div>
							) : (
								<div className="flex h-full min-h-[200px] items-center justify-center text-sm text-muted-foreground">
									กรอกข้อมูลให้ครบเพื่อดูตัวอย่าง
								</div>
							)}
						</div>
					</div>
				</div>

				<DialogFooter className="border-t px-6 py-4">
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						ยกเลิก
					</Button>
					<Button
						onClick={handleApply}
						disabled={!isValid || preview.length === 0}
					>
						สร้าง
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
